<template>
  <div class="auth-page d-flex justify-content-center align-items-center py-4">
    <form class="auth-card shadow-lg p-4 p-md-5" @submit.prevent="handleSubmit">
      <div class="text-center mb-4">
        <h1 class="h5 fw-semibold mb-1">Создание пароля</h1>
        <p class="text-muted small mb-0">Задайте пароль для входа в AI Tenders.</p>
      </div>
      <div v-if="!token" class="alert alert-warning small">Ссылка недействительна. Обратитесь к администратору.</div>
      <template v-else>
        <PasswordStrengthField :password="password">
          <PasswordInput
            v-model="password"
            class="form-control mb-3"
            placeholder="Пароль"
            required
            autocomplete="new-password"
          />
        </PasswordStrengthField>
        <PasswordInput
          v-model="passwordConfirm"
          class="form-control mb-3"
          placeholder="Повтор пароля"
          required
          autocomplete="new-password"
        />
        <div v-if="message" class="alert alert-success py-2 small">{{ message }}</div>
        <div v-if="errorMsg" class="alert alert-danger py-2 small">{{ errorMsg }}</div>
        <button class="btn btn-primary w-100" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2" />
          Создать пароль и войти
        </button>
      </template>
      <p class="text-center small text-muted mt-3 mb-0">
        <router-link to="/login">Вход</router-link>
      </p>
    </form>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/api'
import PasswordInput from '@/components/form/PasswordInput.vue'
import PasswordStrengthField from '@/components/PasswordStrengthField.vue'
import { useAuthStore } from '@/store/pinia.js'
import { isPasswordPolicyMet, passwordPolicyErrors } from '@/utils/passwordPolicy'

const route = useRoute()
const auth = useAuthStore()
const token = computed(() => (route.query.token || '').toString().trim())
const password = ref('')
const passwordConfirm = ref('')
const loading = ref(false)
const message = ref('')
const errorMsg = ref('')

async function handleSubmit() {
  errorMsg.value = ''
  message.value = ''
  if (password.value !== passwordConfirm.value) {
    errorMsg.value = 'Пароли не совпадают.'
    return
  }
  if (!isPasswordPolicyMet(password.value)) {
    errorMsg.value = passwordPolicyErrors(password.value).join('. ') || 'Пароль не соответствует требованиям.'
    return
  }
  loading.value = true
  try {
    const { data } = await api.post('/auth/password-reset/confirm/', {
      token: token.value,
      password: password.value,
      password_confirm: passwordConfirm.value,
    })
    message.value = data?.detail || 'Пароль создан.'
    if (data?.access && data?.refresh) {
      await auth.applyAuthTokens(data.access, data.refresh)
      return
    }
  } catch (e) {
    const d = e?.response?.data
    if (typeof d === 'object' && d) {
      const parts = []
      for (const k of Object.keys(d)) {
        const v = d[k]
        if (Array.isArray(v)) parts.push(...v.map(String))
        else if (typeof v === 'string') parts.push(v)
      }
      errorMsg.value = parts.join(' ') || 'Ссылка недействительна или истекла.'
    } else {
      errorMsg.value = 'Ссылка недействительна или истекла.'
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  padding: 1.5rem;
}
.auth-card {
  width: 100%;
  max-width: 480px;
  border-radius: 1rem;
  background: #fff;
}
</style>
